/**
 * The Desk's restorable reading (#1393 contract 4, session origin, P-4).
 *
 * Mode lives in the address (`?order=calendar`). What a person did on the
 * Desk that an address does not carry (Calendar order's find and Cancelled
 * only, the bulk selection, which topics are open, the scroll position and
 * the row they left from) lives in the Desk entry's history state, the
 * mechanism VisitHost uses for its memory. It is written once, as the person
 * leaves the Desk for a page, so Back and Return land on the Desk as left.
 * A pasted or reloaded session URL has no Desk entry before it and starts
 * from a fresh Desk.
 */
import { createContext, useContext } from "react";

/** History-state key of the Desk's own reading, beside VisitHost's. */
export const DESK_MEMORY = "imamDesk";
/** History-state key on an entry pushed from this course's Desk: Return steps Back to it. */
export const DESK_ORIGIN = "imamDeskOrigin";

export interface DeskReading {
  /** The course and perspective whose Desk wrote it; any other Desk ignores it. */
  owner?: string;
  find?: string;
  cancelledOnly?: boolean;
  selected?: string[];
  openTopics?: string[];
  scrollY?: number;
  /** The row the person left from, focused (never selected) on return. */
  opener?: { box: string; row: string; href?: string; /** Left from the row's open detail rather than its act; the row reopens on return. */ detail?: boolean } | null;
}

/** What the Desk's parts hold right now; written to history as the person leaves. */
export interface DeskMemory {
  /** The reading this Desk entry arrived with, or an empty one. */
  restored: DeskReading;
  /** The live reading, kept current by each part, and what a remounted mode starts from. */
  live: DeskReading;
  /** The restored opener has been returned to; a later remount does not reopen its row. */
  returned: boolean;
}

/** Whose Desk a reading belongs to: one course seen from one perspective. */
export function deskOwner(courseId: string, perspective: string): string {
  return `${courseId}:${perspective}`;
}

export const DeskMemoryContext = createContext<DeskMemory | null>(null);

export function useDeskMemory(): DeskMemory | null {
  return useContext(DeskMemoryContext);
}

/** The reading this history entry carries for `owner`'s Desk, or an empty one. */
export function readDesk(state: unknown, owner: string): DeskReading {
  const own = state && typeof state === "object" ? (state as Record<string, unknown>)[DESK_MEMORY] : null;
  return own && typeof own === "object" && (own as DeskReading).owner === owner ? (own as DeskReading) : {};
}

/** Whether this entry was pushed from course `courseId`'s Desk. */
export function cameFromDesk(state: unknown, courseId: string): boolean {
  return Boolean(state && typeof state === "object" && (state as Record<string, unknown>)[DESK_ORIGIN] === courseId);
}

/** The Desk entry's history state with its reading kept beside VisitHost's memory. */
export function withDeskReading(state: unknown, reading: DeskReading): Record<string, unknown> {
  const own = state && typeof state === "object" ? { ...(state as Record<string, unknown>) } : {};
  own[DESK_MEMORY] = reading;
  return own;
}
